import { DiagramComponent } from '../../core/DiagramComponent';
import { componentRegistry } from '../../core/ComponentRegistry';
import type { GridLayoutConfig, Variables, JXGBoard, JXGElement, BaseComponentConfig } from '../../types/interfaces';

/**
 * Grid Layout Component - Arranges children in a grid pattern
 * 
 * REQUIRED at root level: All diagrams must have GridLayout as the root component.
 * 
 * Properties:
 * - columns: number - Number of columns (default: 1)
 * - spacing: number - Space between items (default: 0.5)
 * - spacingX: number - Horizontal spacing (overrides spacing)
 * - spacingY: number - Vertical spacing (overrides spacing)
 * - rowFactors: number[] - Proportional row height factors (optional)
 * - columnFactors: number[] - Proportional column width factors (optional)
 * - sizeFactors: number[] - Apply same factors to both dimensions (optional)
 * - children: array - Child component configurations (single component per cell)
 * 
 * Sizing Modes:
 * 1. Equal sizing (default): All rows/columns equal size
 * 2. Row height control: Use rowFactors
 * 3. Column width control: Use columnFactors
 * 4. Independent control: Use both rowFactors and columnFactors
 * 5. Unified control: Use sizeFactors for both dimensions
 * 
 * Grid Cells:
 * - Each cell contains EXACTLY ONE child component
 * - To have multiple elements in a cell, nest another GridLayout with columns: 1
 * - Root GridLayout always fills entire canvas (no position property)
 */
class GridLayout extends DiagramComponent<GridLayoutConfig> {
  childComponents: DiagramComponent[];
  elements: JXGElement[];

  constructor(config: GridLayoutConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
    this.childComponents = [];
    this.elements = [];
  }

  /**
   * Calculate proportional sizes based on factors
   * @param factors - Array of proportional factors [1, 2, 1]
   * @param availableSpace - Total space available for distribution
   * @param spacing - Space between elements
   * @returns Array of actual sizes
   */
  private calculateProportionalSizes(factors: number[], availableSpace: number, spacing: number): number[] {
    const count = factors.length;
    const totalSpacing = (count - 1) * spacing;
    const contentSpace = availableSpace - totalSpacing;
    const factorSum = factors.reduce((sum, f) => sum + f, 0);
    
    return factors.map(factor => (factor / factorSum) * contentSpace);
  }

  /**
   * Get cumulative offsets for positioning
   * @param sizes - Array of element sizes
   * @param spacing - Space between elements
   * @returns Array of cumulative offsets [0, size1+spacing, size1+size2+2*spacing, ...]
   */
  private getCumulativeOffsets(sizes: number[], spacing: number): number[] {
    const offsets: number[] = [0];
    for (let i = 0; i < sizes.length - 1; i++) {
      offsets.push(offsets[i] + sizes[i] + spacing);
    }
    return offsets;
  }

  render(): JXGElement[] {
    // For root GridLayout, use canvas boundingbox. For nested, use position.
    const bbox = this.board.getBoundingBox();
    const isRoot = !this.config.position;
    const startX = this.config.position ? Number(this.evaluateExpression(this.config.position[0])) : bbox[0];
    const startY = this.config.position ? Number(this.evaluateExpression(this.config.position[1])) : bbox[1];
    const columns = this.config.columns || 1;
    const defaultSpacing = this.config.spacing !== undefined ? this.config.spacing : 0.5;
    const spacingX = this.config.spacingX !== undefined ? this.config.spacingX : defaultSpacing;
    const spacingY = this.config.spacingY !== undefined ? this.config.spacingY : defaultSpacing;
    const childConfigs = this.config.children || [];

    // Calculate number of rows
    const rows = Math.ceil(childConfigs.length / columns);

    // Determine sizing factors
    let rowFactors: number[] | undefined;
    let columnFactors: number[] | undefined;

    if (this.config.sizeFactors) {
      // Unified mode: same factors for both dimensions
      rowFactors = this.config.sizeFactors.slice(0, rows);
      columnFactors = this.config.sizeFactors.slice(0, columns);
    } else {
      // Independent mode
      rowFactors = this.config.rowFactors;
      columnFactors = this.config.columnFactors;
    }

    // Fill missing factors with 1.0 (equal sizing)
    if (rowFactors && rowFactors.length < rows) {
      rowFactors = [...rowFactors, ...Array(rows - rowFactors.length).fill(1)];
    }
    if (columnFactors && columnFactors.length < columns) {
      columnFactors = [...columnFactors, ...Array(columns - columnFactors.length).fill(1)];
    }

    // Check if factors are all equal (effectively no proportional sizing needed)
    const allEqual = (arr: number[]) => arr.every(v => v === arr[0]);
    const useProportionalColumns = columnFactors && !allEqual(columnFactors);
    const useProportionalRows = rowFactors && !allEqual(rowFactors);

    // Calculate grid dimensions
    // Calculate actual sizes and offsets
    let columnSizes: number[];
    let columnOffsets: number[];
    let rowSizes: number[];
    let rowOffsets: number[];

    if (useProportionalColumns) {
      // Proportional column sizing
      const defaultCellWidth = spacingX;
      const totalDefaultWidth = columns * defaultCellWidth + (columns - 1) * spacingX;
      columnSizes = this.calculateProportionalSizes(columnFactors!, totalDefaultWidth, spacingX);
      columnOffsets = this.getCumulativeOffsets(columnSizes, spacingX);
    } else if (isRoot) {
      // Root GridLayout: fill available canvas width
      const totalWidth = bbox[2] - bbox[0]; // right - left
      const totalSpacing = (columns - 1) * spacingX;
      const cellWidth = (totalWidth - totalSpacing) / columns;
      columnSizes = Array.from({length: columns}, () => cellWidth);
      columnOffsets = this.getCumulativeOffsets(columnSizes, spacingX);
    } else {
      // Nested GridLayout: spacing defines both cell size and step (backward compatible)
      // Offsets: 0, spacing, 2*spacing, 3*spacing, ...
      columnSizes = Array.from({length: columns}, () => spacingX);
      columnOffsets = Array.from({length: columns}, (_, i) => i * spacingX);
    }

    if (useProportionalRows) {
      // Proportional row sizing
      const defaultCellHeight = spacingY;
      const totalDefaultHeight = rows * defaultCellHeight + (rows - 1) * spacingY;
      rowSizes = this.calculateProportionalSizes(rowFactors!, totalDefaultHeight, spacingY);
      rowOffsets = this.getCumulativeOffsets(rowSizes, spacingY);
    } else if (isRoot) {
      // Root GridLayout: fill available canvas height
      const totalHeight = bbox[1] - bbox[3]; // top - bottom
      const totalSpacing = (rows - 1) * spacingY;
      const cellHeight = (totalHeight - totalSpacing) / rows;
      rowSizes = Array.from({length: rows}, () => cellHeight);
      rowOffsets = this.getCumulativeOffsets(rowSizes, spacingY);
    } else {
      // Nested GridLayout: spacing defines both cell size and step (backward compatible)
      // Offsets: 0, spacing, 2*spacing, 3*spacing, ...
      rowSizes = Array.from({length: rows}, () => spacingY);
      rowOffsets = Array.from({length: rows}, (_, i) => i * spacingY);
    }

    // Clear any existing child components by calling their destroy method
    this.childComponents.forEach(child => {
      child.destroy();
    });
    this.childComponents = [];
    this.elements = [];

    // Create each child component at calculated grid positions
    childConfigs.forEach((childConfig, index: number) => {
      const row = Math.floor(index / columns);
      const col = index % columns;
      
      // Calculate cell boundaries
      const cellX = startX + columnOffsets[col];
      const cellY = startY - rowOffsets[row];
      const cellWidth = columnSizes[col];
      const cellHeight = rowSizes[row];

      // Clone child config and determine positioning mode
      let configWithPosition: any;
      
      // Check if child has explicit position (not "auto" and exists)
      const childConfigAny = childConfig as any;
      const hasExplicitPosition = childConfigAny.position && 
        childConfigAny.position !== 'auto' &&
        Array.isArray(childConfigAny.position);
      
      if (childConfig.type === 'slider') {
        // Sliders need [x1, y, x2] format
        if (hasExplicitPosition && childConfigAny.position.length === 3) {
          // Explicit position - treat as relative to cell origin
          const [relX1, relY, relX2] = childConfigAny.position as [number, number, number];
          configWithPosition = {
            ...childConfig,
            position: [cellX + relX1, cellY - relY, cellX + relX2]
          };
        } else {
          // Auto-fill mode - span most of cell width
          const sliderMargin = cellWidth * 0.05;
          configWithPosition = {
            ...childConfig,
            position: [cellX + sliderMargin, cellY - cellHeight / 2, cellX + cellWidth - sliderMargin]
          };
        }
      } else if (childConfig.type === 'gridlayout') {
        // Nested gridlayouts
        if (hasExplicitPosition) {
          // Explicit position - relative to cell origin
          const [relX, relY] = childConfigAny.position as [number, number];
          configWithPosition = {
            ...childConfig,
            position: [cellX + relX, cellY - relY]
          };
        } else {
          // Auto-fill mode - fill entire cell
          const gridConfigAny = childConfig as any;
          configWithPosition = {
            ...childConfig,
            position: [cellX, cellY],
            width: gridConfigAny.width || cellWidth,
            height: gridConfigAny.height || cellHeight
          };
        }
      } else {
        // Standard components (tenframe, text, etc.)
        if (hasExplicitPosition) {
          // Explicit position - relative to cell origin
          const [relX, relY] = childConfigAny.position as [number, number];
          configWithPosition = {
            ...childConfig,
            position: [cellX + relX, cellY - relY]
          };
        } else {
          // Auto-fill mode - center in cell
          configWithPosition = {
            ...childConfig,
            position: [cellX + cellWidth / 2, cellY - cellHeight / 2],
            _cellWidth: cellWidth,
            _cellHeight: cellHeight
          };
        }
      }

      // Get the component class from registry
      const ComponentClass = componentRegistry.components.get(childConfig.type);
      if (ComponentClass) {
        // Pass merged state (parentState + localState) which contains variables
        const childComponent = new ComponentClass(configWithPosition, this.board, this.getState()) as DiagramComponent;
        
        // Setup event propagation from child to parent
        childComponent.onEvent = (event) => {
          this._emitEvent(event.type, event.detail);
        };
        
        const elements = childComponent.render();
        this.childComponents.push(childComponent);
        
        if (Array.isArray(elements)) {
          elements.forEach(el => {
            if (el && !this.elements.includes(el)) {
              this.elements.push(el);
            }
          });
        }
      }
    });

    return this.elements;
  }

  update(variables: Variables): void {
    // Merge new variables into parentState (don't replace entirely)
    this.parentState = { ...this.parentState, ...variables };
    
    // Update existing children instead of destroying/recreating them
    this.childComponents.forEach(child => {
      child.parentState = this.parentState;
      // Only update non-interactive components (sliders handle their own updates)
      if (child.type !== 'slider') {
        child.update(variables);
      }
    });
  }
}

export default GridLayout;
